// Tiny in-memory promise queue indexed by request id.
//
// Used by the api-client step-up middleware to "park" a 403 step_up_required
// response while the user re-authenticates via the StepUpProvider/Dialog.
// Once the dialog calls `resolve(id)` the awaiting middleware retries the
// original request. `reject(id, err)` lets the user cancel.
//
// Pure logic. No React, no DOM. Safe to import anywhere.

/** Per-entry payload threaded by the producer (api-client middleware). */
export interface StepUpQueuePayload {
  /** ACR value the backend says is required for this entry. */
  acr: string;
}

export interface StepUpQueueEntry {
  id: string;
  promise: Promise<void>;
}

export interface StepUpQueue {
  enqueue(payload: StepUpQueuePayload): StepUpQueueEntry;
  resolve(id: string): void;
  reject(id: string, err: unknown): void;
  /** First pending id (FIFO) or null if empty. Pure read; does not remove. */
  peek(): string | null;
  /** Read the payload for a pending entry, or null if not found. */
  getPayload(id: string): StepUpQueuePayload | null;
}

interface PendingEntry {
  id: string;
  payload: StepUpQueuePayload;
  resolve: () => void;
  reject: (err: unknown) => void;
}

function genId(): string {
  // crypto.randomUUID is available in modern browsers, Node 19+, and happy-dom.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback that's good enough for tests / older runtimes.
  return `step-up-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function createStepUpQueue(): StepUpQueue {
  // Use an array so we get FIFO ordering for peek().
  const pending: PendingEntry[] = [];

  return {
    enqueue(payload: StepUpQueuePayload): StepUpQueueEntry {
      const id = genId();
      let resolveFn!: () => void;
      let rejectFn!: (err: unknown) => void;
      const promise = new Promise<void>((res, rej) => {
        resolveFn = res;
        rejectFn = rej;
      });
      pending.push({ id, payload, resolve: resolveFn, reject: rejectFn });
      return { id, promise };
    },
    resolve(id: string): void {
      const idx = pending.findIndex((e) => e.id === id);
      if (idx === -1) return;
      const entry = pending.splice(idx, 1)[0]!;
      entry.resolve();
    },
    reject(id: string, err: unknown): void {
      const idx = pending.findIndex((e) => e.id === id);
      if (idx === -1) return;
      const entry = pending.splice(idx, 1)[0]!;
      entry.reject(err);
    },
    peek(): string | null {
      return pending.length === 0 ? null : pending[0]!.id;
    },
    getPayload(id: string): StepUpQueuePayload | null {
      const entry = pending.find((e) => e.id === id);
      return entry ? entry.payload : null;
    },
  };
}

// Singleton: a single shared queue per application. The api-client middleware
// and the StepUpProvider both reach for this so they can communicate without
// the app needing to thread a queue through providers.
let singleton: StepUpQueue | null = null;
export function getStepUpQueue(): StepUpQueue {
  if (!singleton) singleton = createStepUpQueue();
  return singleton;
}

/** Test helper: wipe the singleton between tests. Not part of the public api. */
export function _resetStepUpQueueForTests(): void {
  singleton = null;
}
